import { useMutation } from '@apollo/client';
import { faDollarSign } from '@fortawesome/free-solid-svg-icons/faDollarSign';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, GenericTooltip, Image, Modal, Panel, Text } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useAnalytics from '../../../lib/hooks/useAnalytics';
import useDealAlert, { GET_ACTIVE_DEAL_ALERT } from '../../../lib/hooks/useDealAlert';
import useFeatureGatedSlashPricing from '../../../lib/hooks/useFeaturedGatedSlashPricing';
import useFeatureGatedDealAlerts from '../../../lib/hooks/useFeatureGatedDealAlerts';
import useProfile from '../../../lib/hooks/useProfile';
import useStationPayAvailability from '../../../lib/hooks/useStationPayAvailability';
import { priceFormatter } from '../../../lib/utils';
import formatDiscount from '../../../lib/utils/formatDiscount';
import { FUEL_POI_VALUE_TO_NAME } from '../../constants';
import ANALYTICS_EVENTS from '../../constants/analyticsEvents';
import StationContext from '../../context/station';
import StationPropType from '../../prop-types/station';
import DealAlertHighlight from '../DealAlertHighlight';
import ExpandableText from '../ExpandableText';
import FuelTypePriceDisplay from '../FuelTypePriceDisplay';
import StationOfferPrice from '../StationOfferPrice';
import styles from './GasPriceCollection.module.css';
import PayRowTooltipContent from './PayRowTooltipContent';

const cx = classnames.bind(styles);

export const ACTIVATE_DEAL = gql`
  mutation ClaimDealAlert($offerId: String!, $stationId: String!){
    claimDealAlert(offerId: $offerId, stationId: $stationId) {
      claimedAt
      offerId
      station {
        id
        name
      }
      submitBy
      transactBy
    }
  }
`;

export default function GasPriceCollection({
  isLoadingPrices,
  showCashPrices,
  station,
}) {
  const { t } = useTranslation(['price', 'fuel']);
  const analytics = useAnalytics();
  const stationContextId = useContext(StationContext);
  const { dealAlert } = useDealAlert();
  const showOffers = useFeatureGatedSlashPricing();
  const showDealAlerts = useFeatureGatedDealAlerts();
  const { isLoggedIn, isPayUser } = useProfile();
  const isPayAvailable = useStationPayAvailability(station);
  const [isModalShowing, setIsModalShowing] = useState(false);

  const {
    fuels = [],
    offers,
    prices = [],
    priceUnit,
    stationId,
  } = station || {};

  const offer = isPayAvailable ? offers?.[0] : undefined;

  const [activateDeal, { loading: activating }] = useMutation(ACTIVATE_DEAL, {
    update: (cache, { data: { claimDealAlert } }) => {
      cache.writeQuery({
        query: GET_ACTIVE_DEAL_ALERT,
        data: { activeDealAlert: claimDealAlert },
      });
    },
    variables: {
      offerId: offer?.id,
      stationId: stationId || stationContextId,
    },
    onCompleted: () => {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.DEAL_ALERT_ACTIVATED,
      });
    },
  });
  const isActivated = !!dealAlert && !!offer && dealAlert.offerId === offer.id;

  const handleExpandDealInfo = useCallback((isExpanded) => {
    if (isExpanded) {
      analytics.tagEvent({
        name: ANALYTICS_EVENTS.DEAL_ALERT_INFO_EXPANDED,
      });
    }
  }, [analytics]);

  const handleActivateDiscount = useCallback(() => {
    if (dealAlert) {
      setIsModalShowing(true);
    } else {
      activateDeal();
    }
  }, [activateDeal, dealAlert]);

  const handleCloseModal = useCallback(() => {
    setIsModalShowing(false);
  }, []);

  const handleReplaceActiveDeal = useCallback(() => {
    // TODO: do we need to deactivate the current deal before activating the new one?
    activateDeal();
    handleCloseModal();
  }, [activateDeal, handleCloseModal]);

  if (!fuels?.length) {
    return null;
  }

  // it may be overkill to filter this, but these are the only fuel types we know will have prices for sure
  const filteredFuels = fuels.filter(fuel => Object.keys(FUEL_POI_VALUE_TO_NAME).includes(fuel));
  const hasGetUpsideDeal = showDealAlerts && offer?.types.includes('getupside');
  const hasGasBuddyDeal = showOffers && offer?.types.includes('gasbuddy');
  const canActivate = hasGetUpsideDeal || (hasGasBuddyDeal && isPayUser);
  const showPayPriceRow = showOffers && offer && (isLoggedIn || hasGetUpsideDeal);

  return (
    <Panel bordered color="white" padded={false}>
      {canActivate && (
        <DealAlertHighlight
          offer={offer}
        />
      )}
      <div className={cx('body')}>
        <div className={cx('column', 'labels')}>
          {showPayPriceRow && (
            <GenericTooltip
              content={(
                <div className={cx('tooltipContent')}>
                  <PayRowTooltipContent isPayUser={isPayUser} seesDeal={hasGetUpsideDeal} />
                </div>
              )}
            >
              <Image alt="GasBuddy™ Gas Card Price" className={cx('icon')} src="https://static.gasbuddy.com/web/pay/svg/card-tilted-left-shadow-v2.svg" />
            </GenericTooltip>
          )}
          <div className={cx('standardPriceLogo', { withOffer: offer })}>
            <GenericTooltip
              content={(
                <div className={cx('tooltipContent')}>
                  <Trans i18nKey="price:standardPriceTooltip">
                    <Text as="p">The prices in this row are the prices that members of the GasBuddy community provide.</Text>
                    <Text as="p">Prices can also come from store owners and retailer data feeds.</Text>
                    <Text as="p">These prices are invaluable to helping one another save the most on fuel!</Text>
                  </Trans>
                </div>
              )}
            >
              <div className={cx('tooltip')}>
                <FontAwesomeIcon icon={faDollarSign} />
              </div>
              <Image
                alt={t('reportedGasPriceImageAlt', { ns: 'price' })}
                className={cx('icon')}
                src="https://static.gasbuddy.com/web/consumer/metric-avatar.svg"
              />
            </GenericTooltip>
          </div>
        </div>

        <div className={cx('collectionContainer')}>
          <div className={cx('row')}>
            {filteredFuels.map(fuel => (
              <div key={`price-${fuel}`} className={cx('column')}>
                <Text className={cx('fuelTypeDisplay')}>
                  {t(`${FUEL_POI_VALUE_TO_NAME[fuel].toLowerCase()}Fuel`, { ns: 'fuel' })}
                </Text>
              </div>
            ))}
          </div>

          {showPayPriceRow && (
            <div className={cx('row')}>
              {filteredFuels.map((fuel) => {
                const { credit } = prices.find(price => price.fuelProduct === fuel) || {};
                const fuelName = FUEL_POI_VALUE_TO_NAME[fuel];
                const discount = offer?.discounts?.find(d => d.grades.includes(fuel));
                const totalDiscount = discount?.pwgbDiscount || discount?.receiptDiscount || 0;
                const formattedDiscount = formatDiscount((totalDiscount * 100).toFixed(0));

                // Prices still need to be shown so users know what can be reported even if non-existent
                return (
                  <div className={cx('column', 'priceDisplay')} key={fuelName}>
                    {/* Only show the flash deal discount if this grade has one */}
                    {canActivate && !!totalDiscount && (
                      <span className={cx('deal', 'pill')}>{formattedDiscount}/gal</span>
                    )}
                    <StationOfferPrice
                      payPrice={credit ? priceFormatter(credit.price - totalDiscount, priceUnit) : 0}
                      regularPriceReport={credit}
                    />
                  </div>
                );
              })}
            </div>
          )}

          <div className={cx('row')}>
            {filteredFuels.map((fuel) => {
              const { cash, credit } = prices.find(price => price.fuelProduct === fuel) || {};
              const fuelName = FUEL_POI_VALUE_TO_NAME[fuel];

              return (
                <div className={cx('column', 'priceDisplay')} key={fuelName}>
                  {/* Prices still need to be shown so users know what can be reported even if non-existent  */}
                  {showCashPrices ? (
                    <FuelTypePriceDisplay cash isLoadingPrices={isLoadingPrices} priceInfo={cash || undefined} />
                  ) : (
                    <FuelTypePriceDisplay isLoadingPrices={isLoadingPrices} priceInfo={credit} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {canActivate && (isLoggedIn
        ? (
          <Modal
            content={(
              <Fragment>
                <Text as="p" bold size="lg">Activate new discount?</Text>
                <Text as="p">You currently have an activated discount. Would you like to clear your existing discount and start a new one?</Text>
                <br />
                <Button onClick={handleReplaceActiveDeal} primary wide>Yes</Button>
                <Button onClick={handleCloseModal} link wide>Cancel</Button>
              </Fragment>
            )}
            forceIsShowing={isModalShowing}
            size="sm"
          >
            {() => (
              <Fragment>
                <ExpandableText className={cx('disclaimer')} onToggle={handleExpandDealInfo}>
                  Only one offer is valid per purchase, and each offer can only be redeemed once.
                  Valid only for purchases using a supported credit or debit card.
                  Offer applicable to credit or debit transactions only.
                  Offer must be claimed before the purchase and purchase made within 4 hours of claiming offer.
                  Offer good at this location only.
                  Offer valid for the first 50 gallons of gas purchased or daily transaction limit.
                  If combined with other discounts, GasBack offer is reduced by up to 3.5 cents per gallon.
                  GasBack amount determined by number of gallons and fuel grade purchased.
                  If the uploaded receipt doesn’t include a grade of gas, you will receive the GasBack applicable for Regular-grade gas.
                  Gas prices shown in-app may be inaccurate due to crowd sourcing.
                  The discount per gallon listed during activation will be accurate.
                </ExpandableText>
                <Button
                  className={cx('btn', { deal: showOffers })}
                  disabled={isActivated}
                  fluid
                  loading={activating}
                  onClick={handleActivateDiscount}
                  primary
                >
                  {isActivated ? 'Discount Activated!' : 'Activate Discount'}
                </Button>
              </Fragment>
            )}
          </Modal>
        )
        : (
          <Button className={cx('btn', { deal: showOffers })} fluid as="a" href="/login" primary>
            Log In To Activate Discount
          </Button>
        )
      )}
    </Panel>
  );
}

GasPriceCollection.propTypes = {
  /** Whether prices are currently being fetched client-side */
  isLoadingPrices: PropTypes.bool,
  showCashPrices: PropTypes.bool,
  station: StationPropType,
};

GasPriceCollection.defaultProps = {
  isLoadingPrices: false,
  showCashPrices: false,
  station: undefined,
};
